/**
 * Reusable annual-target authoring section for any KPI create/add form.
 *
 * Bundles the new measurement fields (verification end date, unit description,
 * baseline + year, target pattern, aggregation, conditional step inputs) and the
 * editable `AnnualTargetGrid`. Driven by a `KpiTargetConfig` value + an onChange
 * patch callback, so it drops into both the dedicated KPI screens and the inline
 * KPI cards inside parent forms (goals/processes/services/unit-goals/…).
 *
 * `buildKpiTargetPayload()` turns the config into the snake_case API fields +
 * `target_values[]` (computed via the shared util) — call it at submit time.
 */
import React from 'react';
import { View, Text, StyleSheet, TextInput, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import Svg, { Path } from 'react-native-svg';
import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getTextAlign, normalizeArabicNumbers } from '../../../utils/rtl';
import SelectDropdown from '../../../components/SelectDropdown';
import type { Frequency, TargetPattern, TargetAggregation } from '../../../types';
import { isPercentUnit, computeGrid, generatePeriods } from '../../../utils/targetGrid';
import AnnualTargetGrid from './AnnualTargetGrid';

export interface KpiTargetConfig {
  verificationEndDate: string;
  unitDescription: string;
  baselineValue: string;
  baselineYear: string;
  targetPattern: TargetPattern;
  targetAggregation: TargetAggregation;
  targetStepPercent: string;
  targetStepAmount: string;
  /** Editable grid values keyed "year:period_index"; non-custom uses "year:1" base. */
  inputs: Record<string, number>;
}

export const emptyKpiTargetConfig: KpiTargetConfig = {
  verificationEndDate: '',
  unitDescription: '',
  baselineValue: '',
  baselineYear: '',
  targetPattern: 'fixed',
  targetAggregation: 'sum',
  targetStepPercent: '',
  targetStepAmount: '',
  inputs: {},
};

/** Hydrate a config from an existing KPI (edit mode), incl. its saved grid. */
export function configFromKpi(kpi: any): KpiTargetConfig {
  const inputs: Record<string, number> = {};
  const rows = kpi?.target_values || [];
  const pattern = (kpi?.target_pattern || 'fixed') as TargetPattern;
  for (const c of rows) {
    inputs[`${c.year}:${c.period_index}`] = parseFloat(c.value) || 0;
  }

  // Non-custom patterns (fixed / increase / decrease) edit a single per-year
  // base keyed "year:1". When a year's first period is dropped (partial start
  // year, e.g. a June start under quarterly → first cell is الربع 2), there is
  // no period_index 1, so seed the base from that year's EARLIEST stored cell.
  // Without this the base row reads an absent "year:1" and renders 0.
  if (pattern !== 'custom' && rows.length > 0) {
    const earliestByYear = new Map<number, { idx: number; val: number }>();
    for (const c of rows) {
      const val = parseFloat(c.value) || 0;
      const cur = earliestByYear.get(c.year);
      if (!cur || c.period_index < cur.idx) {
        earliestByYear.set(c.year, { idx: c.period_index, val });
      }
    }
    for (const [year, { val }] of earliestByYear) {
      inputs[`${year}:1`] = val;
    }
  }

  // Legacy KPIs (created before the annual-grid expansion) carry a single
  // target_value but no target_values rows. Seed the per-year base from
  // target_first_value/target_value so the (fixed) grid shows the real number
  // instead of 0 — and a re-save doesn't zero it out. The backend backfill
  // migration handles already-stored KPIs; this covers the read-path until then.
  if (rows.length === 0) {
    const base =
      kpi?.target_first_value != null
        ? parseFloat(kpi.target_first_value)
        : kpi?.target_value != null
        ? parseFloat(kpi.target_value)
        : NaN;
    const start = kpi?.measurement_start_date || null;
    const end = kpi?.verification_end_date || null;
    const frequency = kpi?.frequency;
    if (Number.isFinite(base) && base !== 0 && frequency && start && end) {
      const years = new Set(
        generatePeriods(start, end, frequency as Frequency).map((p) => p.year),
      );
      // Fixed pattern reads the per-year base at "year:1"; every cell mirrors it.
      for (const year of years) {
        inputs[`${year}:1`] = base;
      }
    }
  }

  return {
    verificationEndDate: kpi?.verification_end_date || '',
    unitDescription: kpi?.unit_description || '',
    baselineValue: kpi?.baseline_value != null ? String(kpi.baseline_value) : '',
    baselineYear: kpi?.baseline_year != null ? String(kpi.baseline_year) : '',
    targetPattern: (kpi?.target_pattern || 'fixed') as TargetPattern,
    targetAggregation: (kpi?.target_aggregation || 'sum') as TargetAggregation,
    targetStepPercent: kpi?.target_step_percent != null ? String(kpi.target_step_percent) : '',
    targetStepAmount: kpi?.target_step_amount != null ? String(kpi.target_step_amount) : '',
    inputs,
  };
}

/** Build the API payload fragment (scalar fields + target_values) from a config. */
export function buildKpiTargetPayload(
  config: KpiTargetConfig,
  ctx: { unit: string; frequency: Frequency | '' | string; startDate: string },
): Record<string, any> {
  const isPercent = isPercentUnit(ctx.unit);
  const aggregation: TargetAggregation = isPercent ? 'last' : config.targetAggregation;
  const isPct = config.targetPattern === 'pct_increase' || config.targetPattern === 'pct_decrease';
  const isNum = config.targetPattern === 'num_increase' || config.targetPattern === 'num_decrease';

  const cells = ctx.frequency
    ? computeGrid({
        frequency: ctx.frequency as Frequency,
        pattern: config.targetPattern,
        unit: ctx.unit,
        start: ctx.startDate || null,
        end: config.verificationEndDate || null,
        stepPercent: parseFloat(config.targetStepPercent) || 0,
        stepAmount: parseFloat(config.targetStepAmount) || 0,
        inputs: config.inputs,
      })
    : [];

  // The earliest period's value acts as the legacy single first/target value.
  const firstBase = cells.length > 0 ? cells[0].value : null;

  return {
    verification_end_date: config.verificationEndDate || null,
    unit_description: config.unitDescription,
    baseline_value: config.baselineValue !== '' ? parseFloat(config.baselineValue).toFixed(2) : null,
    baseline_year: config.baselineYear !== '' ? parseInt(config.baselineYear, 10) : null,
    target_pattern: config.targetPattern,
    target_aggregation: aggregation,
    target_first_value: firstBase != null ? firstBase.toFixed(2) : null,
    target_step_percent: isPct && config.targetStepPercent !== '' ? parseFloat(config.targetStepPercent).toFixed(2) : null,
    target_step_amount: isNum && config.targetStepAmount !== '' ? parseFloat(config.targetStepAmount).toFixed(2) : null,
    ...(cells.length > 0
      ? {
          target_values: cells.map((c) => ({
            year: c.year,
            period_index: c.period_index,
            period_label: c.period_label,
            value: c.value.toFixed(2),
            is_computed: c.is_computed,
          })),
        }
      : {}),
  };
}

/** Client-side validation mirroring the backend rules. Returns field→message. */
export function validateKpiTargetConfig(
  config: KpiTargetConfig,
  ctx: { startDate: string },
): Record<string, string> {
  const e: Record<string, string> = {};
  const pattern = config.targetPattern;
  const hasEnd = !!config.verificationEndDate;
  if (pattern !== 'fixed' && !hasEnd) e.verificationEndDate = 'endDateRequired';
  if (hasEnd && ctx.startDate && config.verificationEndDate < ctx.startDate) e.verificationEndDate = 'endDateAfterStart';
  if (pattern === 'pct_increase' || pattern === 'pct_decrease') {
    const sp = parseFloat(config.targetStepPercent);
    if (config.targetStepPercent === '' || isNaN(sp) || sp <= 0 || sp > 100) e.targetStepPercent = 'stepPercentInvalid';
  }
  if (pattern === 'num_increase' || pattern === 'num_decrease') {
    const sa = parseFloat(config.targetStepAmount);
    if (config.targetStepAmount === '' || isNaN(sa) || sa <= 0) e.targetStepAmount = 'stepAmountInvalid';
  }
  return e;
}

const CalendarIcon = ({ size = 16, color = tokens.colors.raw.gray['500'] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

type MeasurementField = 'startDate' | 'unit' | 'frequency' | 'formula' | 'dataSource';

interface KpiAnnualTargetSectionProps {
  config: KpiTargetConfig;
  onChange: (patch: Partial<KpiTargetConfig>) => void;
  /** Measurement fields owned by the parent KPI state. */
  startDate: string;
  unit: string;
  frequency: Frequency | '' | string;
  /**
   * When provided, the section renders the FULL القياس block (start/end dates,
   * unit, frequency, formula, data source). When omitted (legacy embedded
   * forms that still render their own القياس), only the target-specific fields
   * + grid are shown.
   */
  formula?: string;
  dataSource?: string;
  onMeasurementChange?: (field: MeasurementField, value: string) => void;
  errors?: Record<string, string>;
}

export default function KpiAnnualTargetSection({
  config,
  onChange,
  startDate,
  unit,
  frequency,
  formula = '',
  dataSource = '',
  onMeasurementChange,
  errors = {},
}: KpiAnnualTargetSectionProps) {
  const ownsMeasurement = !!onMeasurementChange;
  const { t } = useTranslation();
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = React.useMemo(() => getStyles(isRTL, colors, isDark), [isRTL, colors, isDark]);

  const isPercent = isPercentUnit(unit);
  const effectiveAggregation: TargetAggregation = isPercent ? 'last' : config.targetAggregation;
  const isPct = config.targetPattern === 'pct_increase' || config.targetPattern === 'pct_decrease';
  const isNum = config.targetPattern === 'num_increase' || config.targetPattern === 'num_decrease';
  const isCustom = config.targetPattern === 'custom';

  const patternOptions = [
    { value: 'fixed', label: t('kpis.patternFixed') },
    { value: 'custom', label: t('kpis.patternCustom') },
    { value: 'pct_increase', label: t('kpis.patternPctIncrease') },
    { value: 'pct_decrease', label: t('kpis.patternPctDecrease') },
    { value: 'num_increase', label: t('kpis.patternNumIncrease') },
    { value: 'num_decrease', label: t('kpis.patternNumDecrease') },
  ];
  const aggregationOptions = isPercent
    ? [{ value: 'last', label: t('kpis.aggLast') }]
    : [
        { value: 'sum', label: t('kpis.aggSum') },
        { value: 'last', label: t('kpis.aggLast') },
      ];

  const unitOptions = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'عدد', label: t('kpis.number') },
    { value: 'درهم', label: t('kpis.currency') },
    { value: 'يوم', label: t('kpis.day') },
  ];
  const frequencyOptions = [
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  const tr = (key?: string) => (key ? t(`validation.${key}`) : undefined);

  const renderDate = (value: string, setValue: (v: string) => void, label: string, required: boolean) => (
    <View style={styles.half}>
      <View style={styles.labelRow}>
        {required && <Text style={styles.star}>*</Text>}
        <Text style={styles.label}>{label}</Text>
      </View>
      <View style={styles.dateBox}>
        {Platform.OS === 'web' ? (
          <input
            type="date"
            value={value}
            onChange={(e: any) => setValue(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              fontSize: tokens.typography.paragraph['paragraph small font size'],
              fontFamily: 'inherit',
              color: colors.textPrimary,
              textAlign: getTextAlign(isRTL),
              direction: 'rtl' as const,
              padding: '10px 0',
              cursor: 'pointer',
            }}
          />
        ) : (
          <>
            <CalendarIcon size={16} color={tokens.colors.raw.slate['500']} />
            <TextInput
              style={styles.input}
              value={value}
              onChangeText={(v) => setValue(v)}
              placeholder="YYYY-MM-DD"
              placeholderTextColor={tokens.colors.raw.gray['400']}
              textAlign={isRTL ? 'right' : 'left'}
            />
          </>
        )}
      </View>
    </View>
  );

  return (
    <View style={styles.wrapper}>
      {ownsMeasurement ? (
        <>
          <Text style={styles.sectionHeading}>{t('kpis.measurement')}</Text>

          {/* Verification end date (left) + start date (right) */}
          <View style={styles.row}>
            {renderDate(config.verificationEndDate, (v) => onChange({ verificationEndDate: v }), t('kpis.verificationEndDate'), config.targetPattern !== 'fixed')}
            {renderDate(startDate, (v) => onMeasurementChange!('startDate', v), t('kpis.measurementStartDate'), true)}
          </View>

          {/* Unit + unit description */}
          <View style={styles.row}>
            <View style={styles.half}>
              <SelectDropdown
                label={t('kpis.measurementUnit')}
                required
                options={unitOptions}
                value={unit}
                placeholder={t('kpis.selectUnitHint')}
                onChange={(v) => onMeasurementChange!('unit', String(v))}
                error={tr(errors.unit)}
              />
            </View>
            <View style={styles.half}>
              <View style={styles.labelRow}>
                <Text style={styles.label}>{t('kpis.unitDescription')}</Text>
              </View>
              <TextInput
                style={styles.input}
                value={config.unitDescription}
                onChangeText={(v) => onChange({ unitDescription: v })}
                placeholder={t('kpis.unitDescriptionHint')}
                placeholderTextColor={tokens.colors.raw.gray['400']}
                textAlign={isRTL ? 'right' : 'left'}
              />
            </View>
          </View>

          {/* Frequency + data source */}
          <View style={styles.row}>
            <View style={styles.half}>
              <SelectDropdown
                label={t('kpis.measurementFrequency')}
                required
                options={frequencyOptions}
                value={frequency}
                placeholder={t('kpis.selectFrequency')}
                onChange={(v) => onMeasurementChange!('frequency', String(v))}
                error={tr(errors.frequency)}
              />
            </View>
            <View style={styles.half}>
              <View style={styles.labelRow}>
                <Text style={styles.label}>{t('kpis.dataSource')}</Text>
              </View>
              <TextInput
                style={styles.input}
                value={dataSource}
                onChangeText={(v) => onMeasurementChange!('dataSource', v)}
                placeholder={t('kpis.dataSourcePlaceholder')}
                placeholderTextColor={tokens.colors.raw.gray['400']}
                textAlign={isRTL ? 'right' : 'left'}
              />
            </View>
          </View>

          {/* Formula (full width) */}
          <View style={styles.fullRow}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>{t('kpis.measurementFormula')}</Text>
            </View>
            <TextInput
              style={styles.input}
              value={formula}
              onChangeText={(v) => onMeasurementChange!('formula', v)}
              placeholder={t('kpis.formulaPlaceholder')}
              placeholderTextColor={tokens.colors.raw.gray['400']}
              textAlign={isRTL ? 'right' : 'left'}
            />
          </View>
        </>
      ) : (
        /* Legacy embedded forms: host renders القياس; section adds end date + unit desc. */
        <View style={styles.row}>
          {renderDate(config.verificationEndDate, (v) => onChange({ verificationEndDate: v }), t('kpis.verificationEndDate'), config.targetPattern !== 'fixed')}
          <View style={styles.half}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>{t('kpis.unitDescription')}</Text>
            </View>
            <TextInput
              style={styles.input}
              value={config.unitDescription}
              onChangeText={(v) => onChange({ unitDescription: v })}
              placeholder={t('kpis.unitDescriptionHint')}
              placeholderTextColor={tokens.colors.raw.gray['400']}
              textAlign={isRTL ? 'right' : 'left'}
            />
          </View>
        </View>
      )}

      {tr(errors.verificationEndDate) && (
        <Text style={styles.error}>{tr(errors.verificationEndDate)}</Text>
      )}

      {/* Baseline + baseline year */}
      <View style={styles.row}>
        <View style={styles.half}>
          <View style={styles.labelRow}>
            <Text style={styles.label}>{t('kpis.baseline')}</Text>
          </View>
          <TextInput
            style={styles.input}
            value={config.baselineValue}
            onChangeText={(v) => onChange({ baselineValue: normalizeArabicNumbers(v) })}
            keyboardType="numeric"
            textAlign={isRTL ? 'right' : 'left'}
            placeholder="0.0"
            placeholderTextColor={tokens.colors.raw.gray['400']}
          />
        </View>
        <View style={styles.half}>
          <View style={styles.labelRow}>
            <Text style={styles.label}>{t('kpis.baselineYear')}</Text>
          </View>
          <TextInput
            style={styles.input}
            value={config.baselineYear}
            onChangeText={(v) => onChange({ baselineYear: normalizeArabicNumbers(v) })}
            keyboardType="numeric"
            textAlign={isRTL ? 'right' : 'left'}
            placeholder="2025"
            placeholderTextColor={tokens.colors.raw.gray['400']}
          />
        </View>
      </View>

      {/* Pattern + aggregation */}
      <View style={styles.row}>
        <View style={styles.half}>
          <SelectDropdown
            label={t('kpis.targetPattern')}
            required
            options={patternOptions}
            value={config.targetPattern}
            placeholder={t('kpis.selectPattern')}
            onChange={(v) => onChange({ targetPattern: v as TargetPattern })}
          />
        </View>
        <View style={styles.half}>
          <SelectDropdown
            label={t('kpis.aggregationMethod')}
            required
            disabled={isPercent}
            options={aggregationOptions}
            value={effectiveAggregation}
            placeholder={t('kpis.selectAggregation')}
            onChange={(v) => onChange({ targetAggregation: v as TargetAggregation })}
          />
        </View>
      </View>

      {/* Conditional step input (only for increase/decrease patterns) */}
      {(isPct || isNum) && (
        <View style={styles.row}>
          <View style={styles.half}>
            <View style={styles.labelRow}>
              <Text style={styles.star}>*</Text>
              <Text style={styles.label}>{isPct ? t('kpis.stepPercent') : t('kpis.stepAmount')}</Text>
            </View>
            <TextInput
              style={[styles.input, (isPct ? errors.targetStepPercent : errors.targetStepAmount) ? styles.inputError : null]}
              value={isPct ? config.targetStepPercent : config.targetStepAmount}
              onChangeText={(v) =>
                onChange(isPct
                  ? { targetStepPercent: normalizeArabicNumbers(v) }
                  : { targetStepAmount: normalizeArabicNumbers(v) })
              }
              keyboardType="numeric"
              textAlign={isRTL ? 'right' : 'left'}
            />
            {tr(isPct ? errors.targetStepPercent : errors.targetStepAmount) && (
              <Text style={styles.error}>{tr(isPct ? errors.targetStepPercent : errors.targetStepAmount)}</Text>
            )}
          </View>
          <View style={styles.half} />
        </View>
      )}

      {/* القيم المستهدفة سنوياً grid */}
      <Text style={styles.title}>{t('kpis.annualTargets')}</Text>
      <AnnualTargetGrid
        frequency={frequency}
        pattern={config.targetPattern}
        aggregation={effectiveAggregation}
        unit={unit}
        start={startDate || null}
        end={config.verificationEndDate || null}
        stepPercent={config.targetStepPercent}
        stepAmount={config.targetStepAmount}
        inputs={config.inputs}
        onInputChange={(key, value) => onChange({ inputs: { ...config.inputs, [key]: value } })}
      />
    </View>
  );
}

const getStyles = (isRTL: boolean, colors: any, isDark: boolean) =>
  StyleSheet.create({
    wrapper: {
      backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate['50'],
      borderRadius: tokens.radii.absolute['radius-8'],
      padding: tokens.spacing.absolute['4'],
      marginTop: tokens.spacing.absolute['4'],
      marginBottom: tokens.spacing.absolute['4'],
    },
    title: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.medium,
      color: colors.textPrimary,
      textAlign: 'center',
      marginBottom: tokens.spacing.absolute['3'],
    },
    sectionHeading: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.medium,
      color: colors.textPrimary,
      textAlign: 'center',
      marginBottom: tokens.spacing.absolute['3'],
    },
    fullRow: {
      marginBottom: tokens.spacing.absolute['3'],
    },
    row: {
      flexDirection: 'row',
      gap: tokens.spacing.absolute['3'],
      marginBottom: tokens.spacing.absolute['3'],
    },
    half: {
      flex: 1,
    },
    labelRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: tokens.spacing.absolute['1'],
      marginBottom: tokens.spacing.absolute['2'],
    },
    label: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      color: colors.textPrimary,
      textAlign: getTextAlign(isRTL),
    },
    star: {
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      color: tokens.colors.raw.red['500'],
    },
    input: {
      flex: 1,
      backgroundColor: colors.card,
      borderWidth: tokens.spacing.absolute['px'],
      borderColor: colors.border,
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['3'],
      paddingVertical: tokens.spacing.absolute['2_5'],
      fontSize: tokens.typography.paragraph['paragraph small font size'],
      fontFamily: FONTS.regular,
      color: colors.textPrimary,
      minHeight: 44,
    },
    inputError: {
      borderColor: tokens.colors.raw.red['500'],
    },
    dateBox: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.card,
      borderWidth: tokens.spacing.absolute['px'],
      borderColor: colors.border,
      borderRadius: tokens.radii.absolute['radius-8'],
      paddingHorizontal: tokens.spacing.absolute['3'],
      gap: tokens.spacing.absolute['2'],
    },
    error: {
      fontSize: tokens.typography.paragraph['paragraph mini font size'],
      fontFamily: FONTS.regular,
      color: tokens.colors.raw.red['500'],
      textAlign: getTextAlign(isRTL),
      marginTop: tokens.spacing.absolute['1'],
    },
  });
